"use client";

import { c as _c } from "react/compiler-runtime";
import UserCICAndLevel, { UserCICAndLevelSize } from "@/components/user/utils/UserCICAndLevel";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { getTimeAgoShort } from "@/helpers/Helpers";
import { areSameProfileIdentity } from "@/helpers/ProfileHelpers";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import useIsTouchDevice from "@/hooks/useIsTouchDevice";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCallback, useState } from "react";
import DropCurationButton from "../DropCurationButton";
import DropMinimalIdentityRow from "../DropMinimalIdentityRow";
import WaveDropActions from "../WaveDropActions";
import WaveDropAuthorPfp from "../WaveDropAuthorPfp";
import WaveDropContent from "../WaveDropContent";
import WaveDropMetadata from "../WaveDropMetadata";
import WaveDropMobileMenu from "../WaveDropMobileMenu";
import WaveDropReactions from "../WaveDropReactions";
import ParticipationIdentityProfileCard from "./ParticipationIdentityProfileCard";
import { getParticipationIdentityProfile, getParticipationVisibleMetadata } from "./participationIdentityProfile.helpers";
import type { DropContentPresentation } from "../dropContentPresentation";
import type { DropIdentityMode, DropInteractionParams } from "../drop.types";
import { DropLocation } from "../drop.types";
interface EndedParticipationDropProps {
  readonly drop: ExtendedDrop;
  readonly showWaveInfo: boolean;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly identityMode?: DropIdentityMode | undefined;
  readonly showInteractions?: boolean | undefined;
  readonly contentPresentation?: DropContentPresentation | undefined;
  readonly embedPath?: readonly string[] | undefined;
  readonly quotePath?: readonly string[] | undefined;
  readonly embedDepth?: number | undefined;
  readonly maxEmbedDepth?: number | undefined;
}
export default function EndedParticipationDrop(t0) {
  const $ = _c(86);
  const {
    drop,
    showWaveInfo,
    activeDrop,
    showReplyAndQuote,
    location,
    onReply,
    onQuoteClick,
    onDropContentClick,
    identityMode: t1,
    showInteractions: t2,
    contentPresentation: t3,
    embedPath,
    quotePath,
    embedDepth,
    maxEmbedDepth
  } = t0;
  const identityMode = t1 === undefined ? "default" : t1;
  const showInteractions = t2 === undefined ? true : t2;
  const contentPresentation = t3 === undefined ? "default" : t3;
  const isActiveDrop = activeDrop?.drop.id === drop.id;
  const router = useRouter();
  let identityProfile;
  let t4;
  let visibleMetadata;
  if ($[0] !== drop.author || $[1] !== drop.metadata || $[2] !== drop.wave) {
    identityProfile = getParticipationIdentityProfile({
      wave: drop.wave,
      metadata: drop.metadata
    });
    let t5;
    if ($[6] !== drop.metadata || $[7] !== drop.wave) {
      t5 = getParticipationVisibleMetadata({
        wave: drop.wave,
        metadata: drop.metadata
      });
      $[6] = drop.metadata;
      $[7] = drop.wave;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    visibleMetadata = t5;
    t4 = identityProfile ? areSameProfileIdentity({
      left: drop.author,
      right: identityProfile
    }) : false;
    $[0] = drop.author;
    $[1] = drop.metadata;
    $[2] = drop.wave;
    $[3] = identityProfile;
    $[4] = t4;
    $[5] = visibleMetadata;
  } else {
    identityProfile = $[3];
    t4 = $[4];
    visibleMetadata = $[5];
  }
  const isSelfNominee = t4;
  const [activePartIndex, setActivePartIndex] = useState(0);
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const [isSlideUp, setIsSlideUp] = useState(false);
  const isMobile = useIsMobileDevice();
  const hasTouch = useIsTouchDevice() || isMobile;
  const showIdentity = identityMode !== "hidden";
  let t5;
  if ($[9] !== router) {
    t5 = (e, path) => {
      e.preventDefault();
      e.stopPropagation();
      router.push(path);
    };
    $[9] = router;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleNavigation = t5;
  let t6;
  if ($[11] !== hasTouch || $[12] !== showInteractions) {
    t6 = () => {
      if (!showInteractions || !hasTouch) {
        return;
      }
      setLongPressTriggered(true);
      setIsSlideUp(true);
    };
    $[11] = hasTouch;
    $[12] = showInteractions;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const handleLongPress = t6;
  let t7;
  if ($[14] !== activePartIndex || $[15] !== drop || $[16] !== onReply) {
    t7 = () => {
      setIsSlideUp(false);
      onReply({
        drop,
        partId: drop.parts[activePartIndex]?.part_id
      });
    };
    $[14] = activePartIndex;
    $[15] = drop;
    $[16] = onReply;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const handleOnReply = t7;
  let t8;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setIsSlideUp(false);
    };
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const handleOnAddReaction = t8;
  const getDropLocationBackground = _temp;
  const dropBackgroundClass = isActiveDrop ? "tw-bg-[#3CCB7F]/10" : getDropLocationBackground();
  const t9 = `${location === DropLocation.WAVE ? "tw-px-4 tw-py-1" : ""} tw-w-full`;
  const t10 = `tw-group tw-relative tw-flex tw-w-full tw-flex-col tw-overflow-hidden tw-rounded-xl tw-px-4 tw-py-3 tw-transition-colors tw-duration-200 tw-ease-linear ${dropBackgroundClass}`;
  let t11;
  if ($[19] !== activePartIndex || $[20] !== drop || $[21] !== handleOnReply || $[22] !== isMobile || $[23] !== showInteractions || $[24] !== showReplyAndQuote) {
    t11 = !isMobile && showInteractions && showReplyAndQuote && <WaveDropActions drop={drop} activePartIndex={activePartIndex} showVoting={false} onReply={handleOnReply} />;
    $[19] = activePartIndex;
    $[20] = drop;
    $[21] = handleOnReply;
    $[22] = isMobile;
    $[23] = showInteractions;
    $[24] = showReplyAndQuote;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== drop || $[27] !== showIdentity) {
    t12 = showIdentity && <WaveDropAuthorPfp drop={drop} />;
    $[26] = drop;
    $[27] = showIdentity;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== drop || $[30] !== handleNavigation || $[31] !== identityMode || $[32] !== showIdentity) {
    t13 = showIdentity && (identityMode === "minimal" ? <DropMinimalIdentityRow drop={drop} /> : <div className="tw-flex tw-flex-col tw-gap-y-2"><div className="tw-flex tw-items-center tw-gap-x-2"><UserCICAndLevel level={drop.author.level} size={UserCICAndLevelSize.SMALL} /><p className="tw-mb-0 tw-text-md tw-font-semibold tw-leading-none"><Link onClick={e_0 => handleNavigation(e_0, `/${drop.author.handle ?? drop.author.primary_address}`)} href={`/${drop.author.handle ?? drop.author.primary_address}`} className="tw-text-iron-200 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-500">{drop.author.handle ?? drop.author.primary_address}</Link></p><div className="tw-size-[3px] tw-flex-shrink-0 tw-rounded-full tw-bg-iron-600" /><p className="tw-mb-0 tw-whitespace-nowrap tw-text-xs tw-font-normal tw-leading-none tw-text-iron-500">{getTimeAgoShort(drop.created_at)}</p></div><div className="tw-flex tw-w-fit tw-items-center tw-whitespace-nowrap tw-rounded-md tw-border tw-border-solid tw-border-iron-500/25 tw-bg-iron-600/10 tw-px-2 tw-py-0.5 tw-font-medium tw-text-iron-500"><span className="tw-text-xs">Participant</span></div></div>);
    $[29] = drop;
    $[30] = handleNavigation;
    $[31] = identityMode;
    $[32] = showIdentity;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== drop.wave || $[35] !== handleNavigation || $[36] !== identityMode || $[37] !== showWaveInfo) {
    t14 = identityMode === "default" && showWaveInfo && (() => {
      const waveMeta = (drop.wave as unknown as {
        chat?: {
          scope?: {
            group?: {
              is_direct_message?: boolean | undefined;
            } | undefined;
          } | undefined;
        } | undefined;
      }).chat;
      const isDirectMessage = waveMeta?.scope?.group?.is_direct_message ?? false;
      const waveHref = getWaveRoute({
        waveId: drop.wave.id,
        isDirectMessage,
        isApp: false
      });
      return <Link href={waveHref} onClick={e_1 => handleNavigation(e_1, waveHref)} className="tw-leading-0 tw-text-[11px] tw-text-iron-500 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-300">{drop.wave.name}</Link>;
    })();
    $[34] = drop.wave;
    $[35] = handleNavigation;
    $[36] = identityMode;
    $[37] = showWaveInfo;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== activePartIndex || $[40] !== contentPresentation || $[41] !== drop || $[42] !== embedDepth || $[43] !== embedPath || $[44] !== handleLongPress || $[45] !== hasTouch || $[46] !== maxEmbedDepth || $[47] !== onDropContentClick || $[48] !== onQuoteClick || $[49] !== quotePath) {
    t15 = <WaveDropContent drop={drop} activePartIndex={activePartIndex} setActivePartIndex={setActivePartIndex} onLongPress={handleLongPress} onDropContentClick={onDropContentClick} onQuoteClick={onQuoteClick} setLongPressTriggered={setLongPressTriggered} isCompetitionDrop={true} hasTouch={hasTouch} contentPresentation={contentPresentation} embedPath={embedPath} quotePath={quotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} />;
    $[39] = activePartIndex;
    $[40] = contentPresentation;
    $[41] = drop;
    $[42] = embedDepth;
    $[43] = embedPath;
    $[44] = handleLongPress;
    $[45] = hasTouch;
    $[46] = maxEmbedDepth;
    $[47] = onDropContentClick;
    $[48] = onQuoteClick;
    $[49] = quotePath;
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  let t16;
  if ($[51] !== t13 || $[52] !== t14 || $[53] !== t15) {
    t16 = <div className="tw-flex tw-w-full tw-flex-col tw-gap-y-2">{t13}{t14}{t15}</div>;
    $[51] = t13;
    $[52] = t14;
    $[53] = t15;
    $[54] = t16;
  } else {
    t16 = $[54];
  }
  let t17;
  if ($[55] !== t12 || $[56] !== t16) {
    t17 = <div className="tw-flex tw-w-full tw-gap-x-3 tw-border-0 tw-bg-transparent tw-text-left">{t12}{t16}</div>;
    $[55] = t12;
    $[56] = t16;
    $[57] = t17;
  } else {
    t17 = $[57];
  }
  let t18;
  if ($[58] !== drop.id || $[59] !== identityProfile || $[60] !== isSelfNominee || $[61] !== showIdentity) {
    t18 = identityProfile && <div className={showIdentity ? "tw-ml-[3.25rem]" : ""}><ParticipationIdentityProfileCard profile={identityProfile} contextId={drop.id} variant="chat" showIdentityHeader={!isSelfNominee} /></div>;
    $[58] = drop.id;
    $[59] = identityProfile;
    $[60] = isSelfNominee;
    $[61] = showIdentity;
    $[62] = t18;
  } else {
    t18 = $[62];
  }
  let t19;
  if ($[63] !== visibleMetadata) {
    t19 = visibleMetadata.length > 0 && <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1"><WaveDropMetadata metadata={visibleMetadata} /></div>;
    $[63] = visibleMetadata;
    $[64] = t19;
  } else {
    t19 = $[64];
  }
  let t20;
  if ($[65] !== drop || $[66] !== showInteractions) {
    t20 = showInteractions && <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1"><DropCurationButton dropId={drop.id} waveId={drop.wave.id} isCuratable={drop.context_profile_context?.curatable ?? false} isCurated={drop.context_profile_context?.curated ?? false} /><WaveDropReactions drop={drop} /></div>;
    $[65] = drop;
    $[66] = showInteractions;
    $[67] = t20;
  } else {
    t20 = $[67];
  }
  let t21;
  if ($[68] !== drop || $[69] !== handleOnReply || $[70] !== isSlideUp || $[71] !== longPressTriggered || $[72] !== showInteractions || $[73] !== showReplyAndQuote) {
    t21 = showInteractions && <WaveDropMobileMenu drop={drop} isOpen={isSlideUp} longPressTriggered={longPressTriggered} showReplyAndQuote={showReplyAndQuote} setOpen={setIsSlideUp} onReply={handleOnReply} onAddReaction={handleOnAddReaction} />;
    $[68] = drop;
    $[69] = handleOnReply;
    $[70] = isSlideUp;
    $[71] = longPressTriggered;
    $[72] = showInteractions;
    $[73] = showReplyAndQuote;
    $[74] = t21;
  } else {
    t21 = $[74];
  }
  let t22;
  if ($[75] !== t10 || $[76] !== t11 || $[77] !== t17 || $[78] !== t18 || $[79] !== t19 || $[80] !== t20 || $[81] !== t21) {
    t22 = <div className={t10}>{t11}{t17}{t18}{t19}{t20}{t21}</div>;
    $[75] = t10;
    $[76] = t11;
    $[77] = t17;
    $[78] = t18;
    $[79] = t19;
    $[80] = t20;
    $[81] = t21;
    $[82] = t22;
  } else {
    t22 = $[82];
  }
  let t23;
  if ($[83] !== t22 || $[84] !== t9) {
    t23 = <div className={t9}>{t22}</div>;
    $[83] = t22;
    $[84] = t9;
    $[85] = t23;
  } else {
    t23 = $[85];
  }
  return t23;
}
function _temp() {
  return "tw-bg-iron-950 tw-ring-1 tw-ring-inset tw-ring-iron-800";
}